import { Link } from "react-router-dom";
import { useSeo } from "~/lib/seo/useSeo";
import {
  CONSENTIMENTO_CONTEUDO,
  CONSENTIMENTO_OFERTAS,
  CONSENTIMENTO_VERSAO,
} from "~/lib/lista/schema";

/**
 * A política de privacidade do site.
 *
 * A que existia em /monet/privacy/ é do aplicativo Monet, em inglês, para as
 * lojas — não serve para a lista de e-mail. Esta é do jgson.net, em
 * português, e trata do único dado pessoal que o site coleta de quem só
 * passa por aqui: o e-mail de quem pede para entrar na lista.
 *
 * Os textos de consentimento vêm do mesmo módulo que o formulário usa, para
 * a política não poder divergir do que a pessoa aceitou na tela.
 */

const ATUALIZADA = "4 de novembro de 2026";

function Secao({
  titulo,
  children,
}: {
  titulo: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-12">
      <h2 className="font-display text-[length:var(--text-step-1)] font-medium leading-tight">
        {titulo}
      </h2>
      <div className="mt-4 space-y-4 leading-relaxed text-jg-fg-muted">
        {children}
      </div>
    </section>
  );
}

export function Privacidade() {
  useSeo({
    title: "Política de privacidade",
    description:
      "O que o jgson.net coleta, por que, por quanto tempo guarda e como você pede para apagar. Em português e sem juridiquês.",
    pathname: "/privacidade",
  });

  return (
    <main className="jg-shell py-16 sm:py-24">
      <div className="max-w-[68ch]">
        <p className="jg-eyebrow">jgson.net</p>
        <h1 className="jg-display mt-3 text-4xl sm:text-5xl">
          Política de privacidade
        </h1>
        <p className="mt-6 text-lg text-jg-fg-muted">
          Atualizada em {ATUALIZADA}. Vale para o site jgson.net. O aplicativo
          Monet tem a{" "}
          <a className="jg-link" href="/monet/privacy/">
            política dele
          </a>
          , separada.
        </p>

        <Secao titulo="Quem é o responsável">
          <p>
            João Guilherme Bittar Vidotto, pessoa física, operando como JGson.
            Para qualquer pedido sobre seus dados:{" "}
            <a className="jg-link" href="mailto:joao@jgson.net">
              joao@jgson.net
            </a>
            . Respondo em até 15 dias, que é o prazo da LGPD.
          </p>
        </Secao>

        <Secao titulo="O que eu coleto, e só isso">
          <p>
            <strong className="text-jg-fg">Seu e-mail</strong>, se você pedir
            para entrar na lista. Junto com ele eu guardo a data e a hora do
            pedido, o seu endereço de IP, o navegador que você usou e o texto
            exato que você aceitou. Isso não é curiosidade: é a prova de que o
            consentimento existiu, que a lei exige de mim e não de você.
          </p>
          <p>
            <strong className="text-jg-fg">O que você escrever</strong> no
            formulário de{" "}
            <Link className="jg-link" to="/hire">
              proposta de trabalho
            </Link>
            : nome, e-mail e a mensagem, para eu poder responder.
          </p>
          <p>
            Navegar pelo site e assistir aos vídeos não pede nada disso. Não há
            cookie de rastreamento, não há pixel de rede social e não há perfil
            de comportamento.
          </p>
        </Secao>

        <Secao titulo="Para que serve o e-mail da lista">
          <p>As finalidades são separadas, e cada uma tem a sua caixa:</p>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong className="text-jg-fg">Obrigatória</strong> — “
              {CONSENTIMENTO_CONTEUDO}”
            </li>
            <li>
              <strong className="text-jg-fg">Opcional</strong> — “
              {CONSENTIMENTO_OFERTAS}”
            </li>
          </ul>
          <p>
            Se você marcou só a primeira, não recebe nada sobre produto pago. A
            base legal é o seu consentimento (artigo 7º, inciso I da LGPD), e a
            redação em vigor é a versão {CONSENTIMENTO_VERSAO}.
          </p>
          <p>
            Nenhuma caixa vem marcada, e a inscrição só existe depois que você
            clica no link que eu mando por e-mail — o duplo opt-in. Até esse
            clique, o endereço fica como pendente e não recebe envio nenhum.
          </p>
        </Secao>

        <Secao titulo="Como sair">
          <p>
            Todo e-mail que eu mando tem link de descadastro no rodapé. Um
            clique, sem formulário, sem “tem certeza?”, e sai na hora. No Gmail
            e no Outlook o botão nativo de cancelar inscrição também funciona.
          </p>
          <p>
            Depois disso eu mantenho o registro de que você esteve inscrito e
            saiu, com as datas — é o que me permite provar que parei de mandar
            quando você pediu. Para apagar tudo, inclusive esse registro, basta
            pedir no e-mail acima.
          </p>
        </Secao>

        <Secao titulo="Onde os dados ficam e por quanto tempo">
          <p>
            Num banco SQLite em um servidor privado na Alemanha (Contabo), atrás
            da Cloudflare. Só eu tenho acesso. Nada vai para planilha, nem para
            ferramenta de automação de marketing, nem para anúncio.
          </p>
          <p>
            Pedido de inscrição que nunca foi confirmado é apagado depois de 7
            dias, quando o link vence. E-mail confirmado fica enquanto você
            quiser receber. Proposta de trabalho fica 5 anos, que é o prazo
            normal de guarda de documento comercial.
          </p>
        </Secao>

        <Secao titulo="Quem mais encosta nisso">
          <p>
            <strong className="text-jg-fg">Resend</strong> entrega os e-mails,
            então vê o seu endereço e o conteúdo da mensagem.{" "}
            <strong className="text-jg-fg">Cloudflare</strong> fica na frente do
            site e vê o IP de quem acessa, como qualquer rede de entrega.{" "}
            <strong className="text-jg-fg">Contabo</strong> hospeda o servidor.
            Os três são operadores: tratam o dado por minha conta e ordem, e não
            podem usá-lo para outra coisa.
          </p>
          <p>
            Os dois estão fora do Brasil, então há transferência internacional.
            Ela acontece com o seu consentimento e sob os contratos padrão de
            proteção de dados de cada fornecedor.
          </p>
        </Secao>

        <Secao titulo="Os seus direitos">
          <p>
            A LGPD te dá direito a confirmar que existe tratamento, acessar os
            dados, corrigir o que está errado, pedir anonimização ou exclusão,
            saber com quem eu compartilhei, revogar o consentimento e levar os
            dados para outro lugar.
          </p>
          <p>
            Para exercer qualquer um deles, mande um e-mail para{" "}
            <a className="jg-link" href="mailto:joao@jgson.net">
              joao@jgson.net
            </a>
            . Não preciso de formulário nem de justificativa, e não cobro nada.
          </p>
        </Secao>

        <Secao titulo="Crianças">
          <p>
            Isto aqui não é feito para menores de 18 anos e eu não coleto dados
            de criança ou adolescente de propósito. Se isso aconteceu, me avise
            e eu apago.
          </p>
        </Secao>

        <Secao titulo="Se isto mudar">
          <p>
            A data no topo muda junto. Se a mudança afetar para que serve o seu
            e-mail, eu aviso por e-mail antes e peço consentimento de novo — não
            vou mudar a finalidade no silêncio.
          </p>
        </Secao>

        <p className="mt-16">
          <Link className="jg-link" to="/">
            Voltar para os vídeos
          </Link>
        </p>
      </div>
    </main>
  );
}
